import type { ReactNode } from "react";
import { cn } from "./cn";

/**
 * ÖĞRENCİ BAĞLAMI ŞERİDİ — "hangi öğrencinin ekranındayım?"
 *
 * ================================================================
 * SEKİZ EKRANIN ÖN KOŞULU, HİÇBİRİNİN PARÇASI DEĞİL
 * ================================================================
 * "Seçili öğrenci" küresel bir durum ve sekiz ekran ona bağlı. Şerit o
 * durumu her ekranda AYNI YERDE ve AYNI BİÇİMDE gösteriyor — çünkü
 * ekran ekran farklı yerde duran bir bağlam göstergesi, kullanıcının
 * her sayfada yeniden araması demek.
 *
 * Teslim paketi bunu envanterde "sayılamayan dört bileşen"den biri
 * olarak listeliyor ve sebebini de yazıyor: sol çizgi + kart deseni
 * bilgi bandıyla aynı belirteçleri kullanıyor, ikisi ölçümle ayırt
 * edilemedi. Sayılamaması yok sayılması değil — desen sekiz ekranda
 * gerçekten tekrarlıyor.
 *
 * ================================================================
 * SOL ÇİZGİ TEK SİNYAL DEĞİL
 * ================================================================
 * `Kart`ın kenar rengi kuralının aynısı: çizgi gözü hızla yönlendiriyor
 * ama bilgiyi metin taşıyor — öğrencinin adı, numarası, sınıfı ve
 * rozetleri zaten yazılı. Renk körlüğünde çizgi kaybolsa da şerit
 * okunmaya devam ediyor.
 */

export interface BaglamSeridiProps {
  /**
   * Baş harfler — "DY".
   *
   * Fotoğraf DEĞİL: profil görseli henüz yok (F-PPL-13) ve olmadığı
   * sürece gri bir yer tutucu, harflerden daha az bilgi taşır.
   */
  basHarfler: string;
  /** Öğrencinin adı */
  ad: string;
  /** Ad ile aynı satırda, soluk — "#93 · 11. sınıf" */
  altBilgi?: ReactNode;
  /** Ad satırının devamındaki rozetler — durum, muvafakat */
  rozetler?: ReactNode;
  /** Şeridin sağ ucundaki eylem — öğrenci değiştir gibi */
  eylem?: ReactNode;
  /** İkinci satır — sınıf, koç, paket bilgisi */
  children?: ReactNode;
  className?: string;
}

export function BaglamSeridi({
  basHarfler,
  ad,
  altBilgi,
  rozetler,
  eylem,
  children,
  className,
}: BaglamSeridiProps) {
  return (
    <section
      aria-label="Seçili öğrenci"
      className={cn(
        /*
          SOL ÇİZGİ 3px ve KENARLIK OLARAK çiziliyor: yerini kaplıyor,
          yani şerit görünüp kaybolduğunda içerik yatay kaymıyor.
          Kartın kendi kenarlığı yok — şerit bir kart değil, sayfanın
          üstünde duran bir BAŞLIK bloğu.
        */
        "rounded-kart border-l-marka-600 flex items-center gap-4 border-l-[3px]",
        "bg-notr-0 shadow-kart px-[18px] py-[15px]",
        className,
      )}
    >
      {/*
        BAŞ HARFLER `aria-hidden`: ekran okuyucu "DY" diye harf harf
        okur ve hemen yanında zaten tam ad yazıyor. İki kez duyurmak
        gürültü.
      */}
      <span
        aria-hidden="true"
        className={cn(
          "rounded-rozet grid size-[42px] shrink-0 place-items-center",
          "bg-marka-50 text-marka-700 text-[15px] font-semibold",
        )}
      >
        {basHarfler}
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {/*
          `flex-wrap`: uzun ad + iki rozet 1024px dar panelde tek satıra
          sığmıyor. Kırpmak yerine alta sarıyor — kırpılan bir rozet
          ("Muvafakat bek…") yasal bir kapıyı yarım gösterirdi.
        */}
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <span className="text-notr-900 truncate text-[17px] font-semibold">{ad}</span>
          {altBilgi && <span className="text-notr-600 text-sm">{altBilgi}</span>}
          {rozetler}
        </div>
        {children}
      </div>

      {/* Eylem küçülmüyor: tıklanamayan bir düğme uzun bir addan kötü. */}
      {eylem && <div className="shrink-0">{eylem}</div>}
    </section>
  );
}
